import type { DeepReadonly, MaybeRefOrGetter } from 'vue'
import type { Form } from '~/ui-blocks/Formalization/blocks/paymentModel/Invoice/Invoice.schema'
import type { Invoice, InvoicesKindEnum, ReceiptModel, ResponseUpload } from '~/utils/receipt-model/ReceiptModel'
import { useToast } from '@solfacil/girassol'
import { reactive, readonly, toValue, watch } from 'vue'
import { InvoiceStatus, PaymentModelStatus } from '~/constants/receiptModel'
import usePaymentModelDeleteFile from '~/store-v2/payment-model/usePaymentModelDeleteFile'
import usePaymentModelUploadFile from '~/store-v2/payment-model/usePaymentModelUploadFile'

type Translate = (key: string) => string

export type InvoiceDocuments = Record<InvoicesKindEnum, ResponseUpload | undefined>
export type InvoiceLoadingStates = Record<InvoicesKindEnum, boolean>

const INVOICE_KINDS: readonly InvoicesKindEnum[] = ['products', 'service_product', 'commission', 'correction_letter']

export function canReplaceInvoice(
  stepStatus: string | null | undefined,
  invoiceStatus: string | null | undefined,
  disableField: boolean,
): boolean {
  if (disableField)
    return false
  if (stepStatus === PaymentModelStatus.IN_PROGRESS)
    return true
  return stepStatus === PaymentModelStatus.TO_REVIEW && invoiceStatus !== InvoiceStatus.APPROVED
}

function toResponseUpload(invoice: Invoice): ResponseUpload {
  return {
    invoice_id: invoice.invoice_id,
    filename: invoice.document_filename || '',
    mimetype: invoice.document_mimetype || '',
    url: invoice.document_url || '',
    url_inline: invoice.document_url_inline || '',
    status: invoice.invoice_status,
    reason: invoice.invoice_reason,
  }
}

function isResponseUpload(value: unknown): value is ResponseUpload {
  return typeof value === 'object' && value !== null && 'invoice_id' in value && typeof value.invoice_id === 'string'
}

export interface UseInvoiceDocumentsResult {
  currentDocuments: DeepReadonly<InvoiceDocuments>
  loadingStates: DeepReadonly<InvoiceLoadingStates>
  upload: (kind: InvoicesKindEnum, file: File) => Promise<void>
  remove: (kind: InvoicesKindEnum) => Promise<boolean>
  updateDocument: (kind: InvoicesKindEnum, file: File | null) => Promise<void>
}

export function useInvoiceDocuments(
  form: Form,
  data: MaybeRefOrGetter<ReceiptModel | undefined>,
  projectId: MaybeRefOrGetter<string>,
  t: Translate,
  // Read-only mode (terminal financing): no upload/delete is sent and no error is forced.
  isReadOnly: MaybeRefOrGetter<boolean> = false,
): UseInvoiceDocumentsResult {
  const { createErrorToast } = useToast()
  const { mutateAsync: mutatePaymentModelUploadFile } = usePaymentModelUploadFile()
  const { mutateAsync: mutatePaymentModelDeleteFile } = usePaymentModelDeleteFile()

  INVOICE_KINDS.forEach(kind => form.defineField(kind))

  const currentDocuments = reactive<InvoiceDocuments>({
    products: undefined,
    service_product: undefined,
    commission: undefined,
    correction_letter: undefined,
  })

  const loadingStates = reactive<InvoiceLoadingStates>({
    products: false,
    service_product: false,
    commission: false,
    correction_letter: false,
  })

  const pendingOperations: Record<InvoicesKindEnum, number> = {
    products: 0,
    service_product: 0,
    commission: 0,
    correction_letter: 0,
  }
  const pendingRemovals = new Map<InvoicesKindEnum, Promise<boolean>>()

  async function track<T>(kind: InvoicesKindEnum, operation: () => Promise<T>): Promise<T> {
    pendingOperations[kind]++
    loadingStates[kind] = true
    try {
      return await operation()
    }
    finally {
      pendingOperations[kind]--
      loadingStates[kind] = pendingOperations[kind] > 0
    }
  }

  async function deleteDocument(kind: InvoicesKindEnum, currentDocument: ResponseUpload): Promise<boolean> {
    try {
      await mutatePaymentModelDeleteFile(currentDocument.invoice_id)
      currentDocuments[kind] = undefined
      form.setFieldValue(kind, null)
      return true
    }
    catch (error) {
      console.error(error)
      createErrorToast(t('receipt-model.invoices.errors.delete_failed'))
      return false
    }
  }

  function remove(kind: InvoicesKindEnum): Promise<boolean> {
    if (toValue(isReadOnly))
      return Promise.resolve(false)

    const pendingRemoval = pendingRemovals.get(kind)
    if (pendingRemoval)
      return pendingRemoval

    const currentDocument = currentDocuments[kind]
    if (!currentDocument)
      return Promise.resolve(true)

    const removal = track(kind, () => deleteDocument(kind, currentDocument))
      .finally(() => pendingRemovals.delete(kind))
    pendingRemovals.set(kind, removal)
    return removal
  }

  function createFormData(kind: InvoicesKindEnum, file: File): FormData {
    const formData = new FormData()
    formData.append('project_id', toValue(projectId))
    formData.append('kind', kind)
    formData.append('file', file, file.name)
    return formData
  }

  async function upload(kind: InvoicesKindEnum, file: File): Promise<void> {
    if (toValue(isReadOnly))
      return

    await track(kind, async () => {
      if (!(await remove(kind)))
        return

      try {
        const uploaded: unknown = await mutatePaymentModelUploadFile(createFormData(kind, file))
        if (!isResponseUpload(uploaded))
          throw new Error('Unexpected response from POST /receipt-model/upload')

        currentDocuments[kind] = uploaded
        form.setFieldValue(kind, uploaded.filename || file.name)
      }
      catch (error) {
        console.error(error)
        createErrorToast(t('receiptModel.alert.upload_not_sent'))
      }
    })
  }

  async function updateDocument(kind: InvoicesKindEnum, file: File | null): Promise<void> {
    if (toValue(isReadOnly))
      return

    if (file) {
      await upload(kind, file)
      return
    }
    await remove(kind)
  }

  watch(() => toValue(data), (paymentModel) => {
    if (!paymentModel)
      return

    const { status, invoice } = paymentModel
    INVOICE_KINDS.forEach((kind) => {
      const invoiceOfKind = invoice?.find(item => item.invoice_kind === kind)
      if (!invoiceOfKind || pendingOperations[kind] > 0)
        return

      form.setFieldValue(kind, invoiceOfKind.document_filename, false)
      currentDocuments[kind] = toResponseUpload(invoiceOfKind)
    })

    if (status === PaymentModelStatus.TO_REVIEW && !currentDocuments.correction_letter && !toValue(isReadOnly)) {
      form.setFieldError('correction_letter', t('form.correnction_letter.helper_text'))
      form.setFieldTouched('correction_letter', true)
    }
  }, { immediate: true })

  return {
    currentDocuments: readonly(currentDocuments),
    loadingStates: readonly(loadingStates),
    upload,
    remove,
    updateDocument,
  }
}
